import { NetworkStatus } from '@apollo/client';
import type { ErrorLike } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { getInLifeQuotingFieldsData } from '@in-life/utils/getInLifeQuotingFieldsData';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { SPAN_SUBMIT_ENERGY_DETAILS } from '@constants/otel';
import { BundleBasketFragment } from '@generated/gql/graphql';
import { useSyncedAction } from '@hooks/useSyncedAction';
import { RequestID } from '@hooks/useSyncedRequest';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useTrace } from '@services/otel';
import { EnergyDetailsFormValues } from '@src/types/energy';
import { JOURNEY_MODE, useMode } from '@store/journey';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  useTrackInLifeEnergyDetailsEdited,
  useTrackSignupEnergyDetailsEdited,
} from '@tracking/trackingEvents';
import {
  selectEnergyQuotes,
  updateEnergyRequirements,
} from '@utils/queries/energyQueries';
import { getBundleBasket } from '@utils/queries/getBasket.ts';

import { EnergyJourneyPaths, signupEnergyPaths } from '../journeyPaths';
import { composeSubmitValues } from './composeSubmitValues';
import { handleEnergyFormInput } from './handleEnergyFormInput.ts';
import { updateBasketPreferences } from './query';
import { updateTrackingContext } from './updateTrackingContext.ts';

const isApiError = (error: unknown): error is ErrorLike => Boolean(error);

const useEnergyDetails = (
  basket: Nullable<BundleBasketFragment>,
  isDetailsFormChanged: boolean,
  journeyPaths: EnergyJourneyPaths = signupEnergyPaths
) => {
  const mode = useMode();
  const { withTrace } = useTrace();
  const navigate = useNavigate();
  const { hasControl, appointment, isSyncedAppointment } =
    useAppointmentContext();

  const [fetchBasket, { networkStatus }] = useLazyQuery(getBundleBasket, {
    fetchPolicy: 'network-only',
  });
  const [
    selectQuote,
    {
      loading: isSelectingQuote,
      error: selectQuoteError,
      reset: resetSelectQuote,
    },
  ] = useMutation(selectEnergyQuotes);
  const [
    updateRequirements,
    {
      loading: isUpdatingRequirements,
      error: requirementsError,
      reset: resetRequirements,
    },
  ] = useMutation(updateEnergyRequirements);
  const [
    updatePreferences,
    {
      loading: isUpdatingPreferences,
      error: preferencesError,
      reset: resetPreferences,
    },
  ] = useMutation(updateBasketPreferences);

  const { updateActionLoading, syncedLoading } = useSyncedAction(
    RequestID.ENERGY_DETAILS_SAVE
  );

  const trackEnergyDetailsEdited = useTrackSignupEnergyDetailsEdited();
  const trackEnergyDetailsEditedInLife = useTrackInLifeEnergyDetailsEdited();
  const { trackingContexts } = useTrackingContext();

  const errors = [requirementsError, preferencesError, selectQuoteError].filter(
    isApiError
  );

  const isRefetchingBasket = networkStatus === NetworkStatus.loading;
  const isLoading = [
    isUpdatingRequirements,
    isUpdatingPreferences,
    isSelectingQuote,
    syncedLoading,
    isRefetchingBasket,
  ].some(Boolean);

  useEffect(() => {
    // Passive clients cannot trigger updates
    // Ensures the basket is refetched when energy details are updated on the active client side
    if (
      appointment?.id &&
      isSyncedAppointment &&
      !hasControl &&
      basket?.id &&
      !syncedLoading
    ) {
      fetchBasket({ variables: { basketId: basket.id } }).catch(() => {
        // errors are captured by errorLink
      });
    }
  }, [
    appointment?.id,
    basket?.id,
    fetchBasket,
    hasControl,
    isSyncedAppointment,
    syncedLoading,
  ]);

  const executeSubmitEnergyDetails = async (
    submitValues: EnergyDetailsFormValues
  ) => {
    if (!basket) {
      throw Error('Energy details submission error. Missing basket context.');
    }

    // Navigate to Energy page straight away and show loading indicator on Tariff section
    navigate(journeyPaths.tariff);

    if (!basket.energy) {
      return navigate(journeyPaths.noEnergyFallback);
    }
    if (!isDetailsFormChanged) {
      return;
    }

    const energyDetails = composeSubmitValues({
      basketId: basket.id,
      energy: basket.energy,
      submitValues,
    });

    let basketResult;
    try {
      basketResult = await updateActionLoading(
        handleEnergyFormInput({
          energyDetails,
          fuelType: submitValues.fuelType,
          energy: basket.energy,
          updateRequirements,
          updatePreferences,
          selectQuote,
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      // Navigates back to Energy Details page if something went wrong
      navigate(journeyPaths.details);
    }

    const updatedTrackingContext = updateTrackingContext(
      trackingContexts,
      basketResult as Nullable<BundleBasketFragment>
    );

    if (mode === JOURNEY_MODE.INLIFE) {
      return trackEnergyDetailsEditedInLife(
        {},
        { contexts: [getInLifeQuotingFieldsData()] }
      );
    }

    trackEnergyDetailsEdited({}, { contexts: updatedTrackingContext });
  };

  const handleSubmit = (submitValues: EnergyDetailsFormValues) =>
    withTrace(SPAN_SUBMIT_ENERGY_DETAILS, () =>
      executeSubmitEnergyDetails(submitValues)
    );

  return {
    errors,
    isLoading,
    handleSubmit,
    resetApiErrors: () => {
      resetRequirements();
      resetPreferences();
      resetSelectQuote();
    },
  };
};

export { useEnergyDetails };
